// Phase notification-count-badge — shared module-level notification count store.
//
// Backed by the same pattern as useSetting: a module-level cache shared by all
// subscribers (BellIcon, AppShell hamburger menu). This prevents duplicate API
// calls when both components mount.
//
// Consumers:
//   - BellIcon — adds 60-second polling; SSE adjustments call inc/dec directly.
//   - AppShell hamburger — shows badge on the Notifications menu item.
//   - NotificationsPage — calls refreshNotificationCount() on mount to clear.

import { useEffect, useState } from 'react';
import { v2 } from '@/api/v2Client';

interface CountStore {
  count: number;
  listeners: Set<() => void>;
}

const store: CountStore = {
  count: 0,
  listeners: new Set(),
};

function notify(): void {
  for (const fn of store.listeners) fn();
}

export function getNotificationCount(): number {
  return store.count;
}

export function setNotificationCount(n: number): void {
  store.count = Math.max(0, n);
  notify();
}

export function incrementNotificationCount(): void {
  setNotificationCount(store.count + 1);
}

export function decrementNotificationCount(): void {
  setNotificationCount(store.count - 1);
}

export function refreshNotificationCount(): void {
  void v2.notifications
    .count()
    .then((res) => setNotificationCount(res.unread))
    .catch(() => {
      // Swallow — badge stays at current value; next poll or SSE will recover.
    });
}

/** Test-only reset. */
export function __resetNotificationCountForTests(): void {
  store.count = 0;
  store.listeners.clear();
}

/**
 * Subscribe to the shared notification count. Returns the current unread count.
 * Re-renders on every store update (store.set / inc / dec / refresh).
 */
export function useNotificationCount(): number {
  const [, force] = useState(0);

  useEffect(() => {
    const tick = (): void => force((n) => n + 1);
    store.listeners.add(tick);
    return () => {
      store.listeners.delete(tick);
    };
  }, []);

  return store.count;
}
